import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';
import { cn } from '@/lib/utils';
import { getNineBoxZone, PerformanceDashboard } from '../../types/dashboard.types';

export interface ScoreOverviewProps {
  dashboard: PerformanceDashboard;
}

const ZONE_LABELS: Record<string, string> = {
  'low-low': 'REALIGN',
  'low-medium': 'REALIGN',
  'medium-low': 'REALIGN',
  'medium-medium': 'DEVELOP',
  'low-high': 'DEVELOP',
  'high-low': 'DEVELOP',
  'medium-high': 'DEVELOP',
  'high-medium': 'DEVELOP',
  'high-high': 'REWARD',
};

/**
 * Nine-box grid is a static categorical placement, not a magnitude chart --
 * per the dataviz skill's guidance, this is rendered as a plain CSS grid
 * rather than a custom SVG, matching Angular's NineBoxGridComponent's actual
 * complexity level (small, not an elaborate visualization).
 */
export const ScoreOverview = ({ dashboard }: ScoreOverviewProps) => {
  const { t } = useTranslation();
  const performanceZone = getNineBoxZone(dashboard.PerformanceAxis);
  const potentialZone = getNineBoxZone(dashboard.PotentialAxis);

  const rows: ('high' | 'medium' | 'low')[] = ['high', 'medium', 'low'];
  const cols: ('low' | 'medium' | 'high')[] = ['low', 'medium', 'high'];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('NINE_BOX_GRID')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-1">
          {rows.map((rowPotential) =>
            cols.map((colPerformance) => {
              const isActive = rowPotential === potentialZone && colPerformance === performanceZone;
              const zoneLabel = ZONE_LABELS[`${colPerformance}-${rowPotential}`];
              return (
                <div
                  key={`${rowPotential}-${colPerformance}`}
                  className={cn(
                    'flex h-16 items-center justify-center rounded-md border text-[11px] font-medium text-muted-foreground',
                    isActive && 'border-primary bg-primary text-primary-foreground'
                  )}
                >
                  {t(zoneLabel)}
                </div>
              );
            })
          )}
        </div>
        <div className="mt-3 flex justify-between text-xs text-muted-foreground">
          <span>{t('PERFORMANCE')} →</span>
          <span>↑ {t('POTENTIAL')}</span>
        </div>
      </CardContent>
    </Card>
  );
};
